import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { AccountCircle, ExpandLess, ExpandMore } from "@mui/icons-material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import { Button, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Icon, Menu, MenuItem } from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled, useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import useMediaQuery from "@mui/material/useMediaQuery";
import { io, Socket } from "socket.io-client";

import { SocketContext } from "../../socket";
import { openInNewTab } from "../../utils/openInNewTab";
import backButton from "../common/Images/backbutton.png";
import logoTop from "../common/svgs/logoTop.svg";

/** 
 * Desktop-only menu configuration
 * Add routes here to show "desktop only" popup on small screens
 * Example: { path: "/shopOwner/reports", title: "Custom Title", message: "Custom message" }
 */
const DESKTOP_ONLY_ROUTES = [
  "/shopOwner/fetchInventories",
  "/shopOwner/salesInventory",
  "/shopOwner/addInventory",
  "/shopOwner/statistics",
  "/shopOwner/salesInsights",
];

const isDesktopOnlyRoute = (navUrl: string | undefined): boolean => {
  if (!navUrl) return false;
  return DESKTOP_ONLY_ROUTES.some(route => navUrl.includes(route));
};

const drawerWidth = 240;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  scrollbarWidth: "none", // Hide scrollbar for Firefox
  // '&::-webkit-scrollbar': {
  //     display: 'none', // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
  // },
  // '&-ms-overflow-style:': {
  //     display: 'none', // Hide the scrollbar for IE
  // },
  marginTop: theme.spacing(5),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

interface SideBarProps {
  sideBarList?: any;
  children?: any;
}

//@ts-ignore
const config = window.config;
const apiBaseURL = config?.server || "http://18.217.95.31:8080";

const SideBar: React.FC<SideBarProps> = ({ children, sideBarList }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("translation");
  const isDesktop = useMediaQuery(theme.breakpoints.up("md")); // md = 900px and up
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  let localData = localStorage.getItem("UserData") ?? "";
  const auth = localData ? JSON.parse(localData) : null;

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  // On desktop: shift main/app bar when drawer is open. On small screens: no shift, drawer overlays.
  const appBarShifted = isDesktop && open;
  const mainFullWidth = !isDesktop || open; // main has no left margin when mobile (overlay) or when desktop drawer open
  const mainShifted = mainFullWidth; // alias for Main open prop (same value)

  const [expand, setExpand] = React.useState({ open: "" });
  const [desktopOnlyAlertOpen, setDesktopOnlyAlertOpen] = React.useState(false);
  const [popupBlockedAlertOpen, setPopupBlockedAlertOpen] = React.useState(false);

  const handleClick = (id: any) => {
    setExpand({ open: expand.open === id ? "" : id });
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const logoutHandler = () => {
    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position='fixed' open={appBarShifted}>
        <Toolbar
          style={{ backgroundColor: "#074470", display: "flex", justifyContent: "space-between" }}
        >
          <Grid sx={{ float: "left" }}>
            <IconButton
              color='inherit'
              aria-label='open drawer'
              onClick={handleDrawerOpen}
              edge='start'
              sx={{ mr: 2, ...(open && { color: "white" }) }}
            >
              <MenuIcon style={{ color: "white" }} />
            </IconButton>
          </Grid>
          <Grid sx={{ float: "right", display: "flex", alignItems: "center" }}>
            <span
              style={{
                color: "white",
                fontWeight: 600,
                fontSize: "1rem",
                lineHeight: "1.5",
              }}
            >
              {auth?.storename}
            </span>
            <IconButton
              size='large'
              aria-label='account of current user'
              aria-controls='menu-appbar'
              aria-haspopup='true'
              onClick={handleMenu}
              color='inherit'
            >
              <AccountCircle style={{ color: "white" }} />
            </IconButton>
            <Menu
              id='menu-appbar'
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              {auth?.role === "SHOPOWNER" && (
                <MenuItem
                  onClick={() => {
                    navigate("/shopOwner/profile");
                    handleClose();
                  }}
                >
                  My account
                </MenuItem>
              )}
              <MenuItem onClick={logoutHandler}>{t("logout")}</MenuItem>
            </Menu>
          </Grid>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            backgroundColor: "#074470",
            color: "white",
          },
        }}
        variant={isDesktop ? "persistent" : "temporary"}
        anchor='left'
        open={open}
        onClose={handleDrawerClose}
        ModalProps={{ keepMounted: true }}
      >
        <DrawerHeader sx={{ justifyContent: "space-evenly" }}>
          {/* Lottery System */}
          <img src={logoTop} alt='logo top' height='50%' width='60%' 
          // onClick={() => navigate('/')} 
          style={{
            cursor: "pointer"
          }} />
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "ltr" ? (
              <ChevronLeftIcon
                style={{
                  color: "white",
                }}
              />
            ) : (
              <ChevronRightIcon
                style={{
                  color: "white",
                }}
              />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List
          sx={{ width: "100%", maxWidth: 360 }}
          component='nav'
          aria-labelledby='nested-list-subheader'
        >
          {sideBarList.map((item: any, index: any) => {
            return (
              <div key={index}>
                <ListItemButton
                  key={index}
                  onClick={() => {
                    if (item.sublist) {
                      handleClick(index);
                    } else {
                      if (!isDesktop && isDesktopOnlyRoute(item.navUrl)) {
                        setDesktopOnlyAlertOpen(true);
                        return;
                      }
                      navigate(item.navUrl);
                      if (!isDesktop) handleDrawerClose();
                    }
                  }}
                >
                  <ListItemIcon
                    style={{ display: "flex", justifyContent: "flex-end", color: "white" }}
                  >
                    {item.icon && item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.list} />
                  {item.sublist ? expand.open === index ? <ExpandLess /> : <ExpandMore /> : null}
                </ListItemButton>
                {item?.sublist &&
                  item.sublist?.map((i: any, subIndex: number) => {
                    return (
                      <Collapse key={i?.navUrl || i?.name || subIndex} in={expand.open === index} timeout='auto' unmountOnExit>
                        <List component='div' disablePadding>
                          <ListItemButton
                            sx={{ textAlign: "left", marginLeft: "25%" }}
                            onClick={() => {
                              if (!isDesktop && isDesktopOnlyRoute(i?.navUrl)) {
                                setDesktopOnlyAlertOpen(true);
                                return;
                              }
                              // a screen entry opens its display in its own tab, so
                              // several screens can be shown at the same time
                              if (i?.newTab) {
                                if (!openInNewTab(i?.navUrl)) {
                                  setPopupBlockedAlertOpen(true);
                                }
                                if (!isDesktop) handleDrawerClose();
                                return;
                              }
                              navigate(i?.navUrl);
                              if (!isDesktop) handleDrawerClose();
                            }}
                          >
                            <ListItemText primary={i.label ?? t(i.name)} />
                          </ListItemButton>
                        </List>
                      </Collapse>
                    );
                  })}
              </div>
            );
          })}
        </List>

        <Grid container display={"flex"} justifyContent={"center"} alignContent={"center"} flexDirection={"column"} style={{
          marginTop: "auto",
          overflow: "hidden",
        }}>
          <Grid item>
            <span style={{
              fontFamily: "Baloo 2",
              fontSize: "20px",
              fontWeight: 600,
              lineHeight: "20px",
              textAlign: "left",
              textUnderlinePosition: "from-font",
              textDecorationSkipInk: "none",
              color: "#F17400"
            }}>Support/Help</span>
          </Grid>
          <Grid item>
            <span style={{
              fontFamily: "Baloo 2",
              fontSize: "20px",
              fontWeight: 600,
              lineHeight: "20px",
              textAlign: "left",
              textUnderlinePosition: "from-font",
              textDecorationSkipInk: "none",
            }}><a href="tel:8333141825">833-314-1825</a></span>
          </Grid>
        </Grid>
      </Drawer>
      <Main open={mainFullWidth}>{children}</Main>

      <Dialog
        open={desktopOnlyAlertOpen}
        onClose={() => setDesktopOnlyAlertOpen(false)}
        aria-labelledby="desktop-only-alert-title"
        aria-describedby="desktop-only-alert-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 24,
            minWidth: { xs: "80vw", sm: 360 },
          },
        }}
      >
        <DialogTitle id="desktop-only-alert-title" sx={{ fontWeight: 600, fontSize: "1.25rem" }}>
          Desktop only
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="desktop-only-alert-description">
            This option is available only on Desktop.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setDesktopOnlyAlertOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* A screen entry opens in a new tab, which the browser can block */}
      <Dialog
        open={popupBlockedAlertOpen}
        onClose={() => setPopupBlockedAlertOpen(false)}
        aria-labelledby="popup-blocked-alert-title"
        PaperProps={{ sx: { borderRadius: 2, boxShadow: 24, minWidth: { xs: "80vw", sm: 360 } } }}
      >
        <DialogTitle id="popup-blocked-alert-title" sx={{ fontWeight: 600, fontSize: "1.25rem" }}>
          {t("popupBlockedTitle", "Pop-up blocked")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("popupBlockedSidebar", "Please allow pop-ups for this site so the screen can open in a new tab.")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setPopupBlockedAlertOpen(false)} sx={{ textTransform: "none", fontWeight: 600 }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default SideBar;
